import { Container, RichText, SocialSharing, StructuredData } from '@/components';
import { BlogCard } from '@/components/Blog';
import {
  ArticleContent,
  ArticleDate,
  ArticleMeta,
  ArticleTagList,
  ArticleTitle,
  ArticleWrapper,
  BlogGrid,
  RelatedArticlesSection,
  RelatedArticlesTitle,
  TagChip,
  TagChipText,
} from '@/components/Blog/Blog.styles';
import { formatBlogDate } from '@/components/Blog/BlogCard';
import { BLOG_COPY, getBlogTagLabel } from '@/constants';
import { getBlogPostBySlug, getRelatedBlogPosts } from '@/fetchers';
import type { IBlogPost, IBlogPostCard } from '@/interfaces';
import {
  buildBlogPath,
  checkIsPreviewModeRequest,
  getBlogPostPath,
  getNonWebpOgImageUrl,
  setPublicPageCacheHeader,
} from '@/utils';
import { extractExcerptFromRichText, getArticleSchema } from '@/utils/seoPageSchemas.utils';
import type { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

interface IBlogPostServerSideProps {
  post: IBlogPost;
  relatedPosts: IBlogPostCard[];
}

const BlogPostPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ post, relatedPosts }) => {
  const pageUrl = `${publicRuntimeConfig.origin}${getBlogPostPath(post.slug)}`;
  const excerpt = extractExcerptFromRichText(post.content?.json);

  const metaTitle = post.seo?.title || `${post.title} | Parcel Monitor`;
  const metaDescription = post.seo?.description || excerpt;
  const ogImageUrl = getNonWebpOgImageUrl(post.seo?.ogImage?.url ? post.seo.ogImage : post.thumbnail);

  const articleSchema = getArticleSchema({
    headline: post.title,
    datePublished: post.publishedDate,
    dateModified: post.sys.publishedAt,
    image: ogImageUrl,
  });

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        canonical={pageUrl}
        noindex={post.seo?.robots?.includes('noindex')}
        nofollow={post.seo?.robots?.includes('nofollow')}
        openGraph={{
          url: pageUrl,
          title: post.seo?.ogTitle || metaTitle,
          description: post.seo?.ogDescription || metaDescription,
          type: 'article',
          article: {
            publishedTime: post.publishedDate,
            modifiedTime: post.sys.publishedAt,
            tags: post.tags ?? undefined,
          },
          images: ogImageUrl
            ? [{ url: ogImageUrl, alt: post.thumbnail?.description || post.title }]
            : [{ url: `${publicRuntimeConfig.origin}/images/parcel_monitor_og_image.png` }],
        }}
      />
      <StructuredData data={articleSchema} />
      <Container>
        <ArticleWrapper>
          {!!post.tags?.length && (
            <ArticleTagList>
              {post.tags.map((tag) => (
                <li key={tag}>
                  <TagChip href={buildBlogPath({ tag })}>
                    <TagChipText>{getBlogTagLabel(tag)}</TagChipText>
                  </TagChip>
                </li>
              ))}
            </ArticleTagList>
          )}
          <ArticleTitle>{post.title}</ArticleTitle>
          <ArticleMeta>
            <ArticleDate dateTime={post.publishedDate}>{formatBlogDate(post.publishedDate)}</ArticleDate>
          </ArticleMeta>

          {/* No hero image: the thumbnail is the listing card and og:image only —
              an article illustrates itself from inside its own content. */}
          {post.content && (
            <ArticleContent>
              <RichText richText={post.content} pageTitle={post.title} />
            </ArticleContent>
          )}

          <SocialSharing url={pageUrl} title={post.title} />

          {relatedPosts.length > 0 && (
            <RelatedArticlesSection>
              <RelatedArticlesTitle>{BLOG_COPY.relatedArticles}</RelatedArticlesTitle>
              <BlogGrid>
                {relatedPosts.map((relatedPost) => (
                  <BlogCard key={relatedPost.slug} post={relatedPost} />
                ))}
              </BlogGrid>
            </RelatedArticlesSection>
          )}
        </ArticleWrapper>
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<IBlogPostServerSideProps> = async ({ params, query, res }) => {
  const slug = typeof params?.slug === 'string' ? params.slug : '';
  if (!slug) return { notFound: true };

  const isPreviewMode = checkIsPreviewModeRequest(query);
  const post = await getBlogPostBySlug({ slug, isPreviewMode });

  if (!post) return { notFound: true };

  const relatedPosts = post.tags?.length
    ? await getRelatedBlogPosts({ tags: post.tags, excludeSlug: slug, isPreviewMode })
    : [];

  setPublicPageCacheHeader(res, isPreviewMode);

  return {
    props: {
      post,
      relatedPosts,
    },
  };
};

export default BlogPostPage;
